import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd } from "../lib/format";
import { Icon } from "../components/icons";
import { Avatar, Empty } from "../components/ui";
import type { WorkItem, WorkKind, Urgency } from "../lib/workClient";
import * as work from "../lib/workClient";

/* The Cockpit — a CSM's prioritized daily worklist. One ranked queue fused
 * from churn, renewals, signals, tasks, and expansion, each with the reason
 * and the recommended next action. Filter to "my book" or the whole team,
 * and jump straight to the account to act. */

const KIND: Record<WorkKind, { label: string; icon: string; color: string }> = {
  churn: { label: "Churn risk", icon: "pulse", color: "var(--risk)" },
  renewal: { label: "Renewal", icon: "refresh", color: "var(--corona-ink)" },
  signal: { label: "Risk signal", icon: "bolt", color: "var(--watch)" },
  task: { label: "Task", icon: "check", color: "var(--muted)" },
  expansion: { label: "Expansion", icon: "arrowUp", color: "var(--good)" },
};
const URG: Record<Urgency, { label: string; cls: string }> = {
  now: { label: "Now", cls: "risk" }, soon: { label: "Soon", cls: "watch" }, watch: { label: "Watch", cls: "neutral" },
};

export default function Cockpit() {
  const { ws, me } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [cockpit, setCockpit] = useState<work.Cockpit | null>(null);
  const [scope, setScope] = useState<"mine" | "team">("mine");
  const [kind, setKind] = useState<WorkKind | "all">("all");

  useEffect(() => {
    work.loadCockpit(ws, scope === "mine" ? me.id : undefined).then((r) => { setMode(r.mode); setCockpit(r.cockpit); });
  }, [ws, me.id, scope]);

  const items = useMemo(() => (cockpit?.items ?? []).filter((i) => kind === "all" || i.kind === kind), [cockpit, kind]);
  const s = cockpit?.summary;
  const ownerName = (id: string) => ws.users.find((u) => u.id === id)?.name ?? "—";

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Focus</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Cockpit</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Your prioritized worklist — churn, renewals, signals, tasks, and expansion fused into one ranked queue. Each item tells you why it's here and what to do next.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className={`mode-pill ${mode === "api" ? "live" : ""}`}><i />{mode === "api" ? "Live API" : "Local demo"}</span>
          <div className="seg-control">
            <button className={scope === "mine" ? "on" : ""} onClick={() => setScope("mine")}>My book</button>
            <button className={scope === "team" ? "on" : ""} onClick={() => setScope("team")}>Whole team</button>
          </div>
        </div>
      </div>

      <div className="stats">
        <div className="stat"><div className="stat__label"><Icon name="clock" size={14} /> Do now</div><div className="stat__value" style={{ color: "var(--risk)" }}>{s?.now ?? 0}</div><div className="stat__foot">urgent items</div></div>
        <div className="stat"><div className="stat__label"><Icon name="calendar" size={14} /> Coming up</div><div className="stat__value" style={{ color: "var(--watch)" }}>{s?.soon ?? 0}</div><div className="stat__foot">soon</div></div>
        <div className="stat"><div className="stat__label"><Icon name="target" size={14} /> ARR needing action now</div><div className="stat__value">{usd(s?.arrNow ?? 0)}</div><div className="stat__foot">across urgent items</div></div>
        <div className="stat"><div className="stat__label"><Icon name="layers" size={14} /> Total queue</div><div className="stat__value">{s?.total ?? 0}</div><div className="stat__foot">{scope === "mine" ? "in your book" : "across the team"}</div></div>
      </div>

      <div className="row mb-24" style={{ gap: 8, flexWrap: "wrap" }}>
        <button className={`chip ${kind === "all" ? "chip--active" : ""}`} onClick={() => setKind("all")}>All ({cockpit?.summary.total ?? 0})</button>
        {(Object.keys(KIND) as WorkKind[]).map((k) => (
          <button key={k} className={`chip ${kind === k ? "chip--active" : ""}`} onClick={() => setKind(k)}>
            <Icon name={KIND[k].icon} size={13} /> {KIND[k].label} ({cockpit?.summary.byKind[k] ?? 0})
          </button>
        ))}
      </div>

      {!cockpit ? <Empty>Loading your worklist…</Empty> : items.length === 0 ? (
        <div className="card"><div className="card__bd"><Empty>Nothing in this filter — inbox zero. 🎉</Empty></div></div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {items.map((i) => <WorkCard key={i.id} item={i} ownerName={ownerName(i.ownerId)} showOwner={scope === "team"} />)}
        </div>
      )}
    </div>
  );
}

function WorkCard({ item, ownerName, showOwner }: { item: WorkItem; ownerName: string; showOwner: boolean }) {
  const k = KIND[item.kind];
  const u = URG[item.urgency];
  return (
    <div className="card" style={{ borderLeft: `3px solid ${u.cls === "risk" ? "var(--risk)" : u.cls === "watch" ? "var(--watch)" : "var(--line)"}` }}>
      <div className="card__bd" style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px" }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--surface-2)", display: "grid", placeItems: "center", color: k.color, flexShrink: 0 }}>
          <Icon name={k.icon} size={17} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
            <span className={`badge badge--${u.cls}`}>{u.label}</span>
            <span className="tag">{k.label}</span>
            <b style={{ fontSize: 14.5 }}>{item.title}</b>
          </div>
          <div className="faint" style={{ fontSize: 12.5 }}>{item.reason}</div>
          <div style={{ fontSize: 12.5, marginTop: 4, color: "var(--ink-2)" }}><Icon name="arrowRight" size={12} /> {item.action}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {item.arr > 0 && <div style={{ fontFamily: "var(--serif)", fontSize: 17 }}>{usd(item.arr)}</div>}
          {showOwner && <div className="row" style={{ gap: 5, justifyContent: "flex-end", marginTop: 4 }}><Avatar name={ownerName} size="sm" /><span className="faint" style={{ fontSize: 11.5 }}>{ownerName}</span></div>}
        </div>
        <Link className="btn btn--ghost btn--sm" to={`/accounts/${item.accountId}`} style={{ flexShrink: 0 }}>Open</Link>
      </div>
    </div>
  );
}
